/**
 * MonitorThresholdsPanel — admin tuning of RAELA escalation thresholds,
 * strike decay windows, and send cooldowns.
 *
 * All values persist in the singleton raela_monitor_settings row and are
 * read live by fn_record_strike + send-cooldown checks.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useMonitorSettings, type MonitorSettings } from "@/lib/moderation/useMonitorSettings";
import { toast } from "@/hooks/use-toast";
import { Save, RotateCcw } from "lucide-react";

const DEFAULTS: Omit<MonitorSettings, "id" | "updated_at" | "updated_by"> = {
  warn_decay_days: 90,
  crisis_decay_days: null,
  block_decay_days: null,
  mute_threshold: 3,
  mute_duration_hours: 24,
  suspend_threshold: 5,
  high_priority_threshold: 3,
  critical_priority_threshold: 5,
  send_cooldown_seconds: 30,
  send_cooldown_window_hours: 1,
};

const NumberField = ({
  label,
  hint,
  value,
  min = 0,
  onChange,
  suffix,
}: {
  label: string;
  hint?: string;
  value: number;
  min?: number;
  onChange: (v: number) => void;
  suffix?: string;
}) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-28"
      />
      {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
    </div>
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const NullableDecay = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) => {
  const enabled = value != null;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{enabled ? "decays" : "permanent"}</span>
          <Switch
            checked={enabled}
            onCheckedChange={(on) => onChange(on ? 365 : null)}
          />
        </div>
      </div>
      {enabled && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            value={value ?? 0}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-28"
          />
          <span className="text-xs text-muted-foreground">days until expiry</span>
        </div>
      )}
    </div>
  );
};

export const MonitorThresholdsPanel = () => {
  const { settings, loading, error, save } = useMonitorSettings();
  const [draft, setDraft] = useState<MonitorSettings | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings) setDraft(settings);
  }, [settings]);

  if (loading || !draft) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;

  const set = <K extends keyof MonitorSettings>(k: K, v: MonitorSettings[K]) =>
    setDraft({ ...draft, [k]: v });

  const onSave = async () => {
    if (draft.high_priority_threshold > draft.critical_priority_threshold) {
      toast({ title: "Critical threshold must be ≥ high threshold", variant: "destructive" });
      return;
    }
    if (draft.mute_threshold > draft.suspend_threshold) {
      toast({ title: "Suspend threshold must be ≥ mute threshold", variant: "destructive" });
      return;
    }
    setBusy(true);
    const { error: err } = await save({
      warn_decay_days: draft.warn_decay_days,
      crisis_decay_days: draft.crisis_decay_days,
      block_decay_days: draft.block_decay_days,
      mute_threshold: draft.mute_threshold,
      mute_duration_hours: draft.mute_duration_hours,
      suspend_threshold: draft.suspend_threshold,
      high_priority_threshold: draft.high_priority_threshold,
      critical_priority_threshold: draft.critical_priority_threshold,
      send_cooldown_seconds: draft.send_cooldown_seconds,
      send_cooldown_window_hours: draft.send_cooldown_window_hours,
    });
    setBusy(false);
    toast(
      err
        ? { title: "Save failed", description: err, variant: "destructive" }
        : { title: "Thresholds updated" },
    );
  };

  const onReset = () => setDraft({ ...draft, ...DEFAULTS });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-medium">Escalation thresholds & cooldowns</h3>
        <p className="text-sm text-muted-foreground mt-1">
          These dials control how RAELA's strike ladder behaves. Changes take effect
          immediately for the next classified message.
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <h4 className="text-sm font-semibold">Strike decay</h4>
        <div className="grid gap-4 sm:grid-cols-3">
          <NumberField
            label="Warn decay"
            hint="How long a 'warn' strike counts toward escalation."
            value={draft.warn_decay_days}
            min={1}
            onChange={(v) => set("warn_decay_days", v)}
            suffix="days"
          />
          <NullableDecay
            label="Crisis decay"
            value={draft.crisis_decay_days}
            onChange={(v) => set("crisis_decay_days", v)}
          />
          <NullableDecay
            label="Block decay"
            value={draft.block_decay_days}
            onChange={(v) => set("block_decay_days", v)}
          />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h4 className="text-sm font-semibold">Auto-action thresholds</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberField
            label="Mute threshold"
            hint="Active warns that trigger a temporary community mute."
            value={draft.mute_threshold}
            min={1}
            onChange={(v) => set("mute_threshold", v)}
            suffix="active warns"
          />
          <NumberField
            label="Mute duration"
            value={draft.mute_duration_hours}
            min={1}
            onChange={(v) => set("mute_duration_hours", v)}
            suffix="hours"
          />
          <NumberField
            label="Suspend threshold"
            hint="Active warns that lock the account (moderation_locked_at)."
            value={draft.suspend_threshold}
            min={1}
            onChange={(v) => set("suspend_threshold", v)}
            suffix="active warns"
          />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h4 className="text-sm font-semibold">Mod queue priority</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberField
            label="High priority above"
            value={draft.high_priority_threshold}
            min={1}
            onChange={(v) => set("high_priority_threshold", v)}
            suffix="active warns"
          />
          <NumberField
            label="Critical priority above"
            value={draft.critical_priority_threshold}
            min={1}
            onChange={(v) => set("critical_priority_threshold", v)}
            suffix="active warns"
          />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h4 className="text-sm font-semibold">Send cooldown</h4>
        <p className="text-xs text-muted-foreground">
          After any warn-or-worse verdict, the sender is throttled. Set seconds to 0 to disable.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberField
            label="Cooldown duration"
            value={draft.send_cooldown_seconds}
            min={0}
            onChange={(v) => set("send_cooldown_seconds", v)}
            suffix="seconds"
          />
          <NumberField
            label="Cooldown window"
            hint="Recent-warn lookback window for repeated cooldowns."
            value={draft.send_cooldown_window_hours}
            min={1}
            onChange={(v) => set("send_cooldown_window_hours", v)}
            suffix="hours"
          />
        </div>
      </Card>

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button variant="ghost" onClick={onReset}>
          <RotateCcw className="h-4 w-4" /> Reset to defaults
        </Button>
        <Button onClick={onSave} disabled={busy}>
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save thresholds"}
        </Button>
      </div>
    </div>
  );
};

export default MonitorThresholdsPanel;
